import React, { useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Pagination, Spinner, Table } from "react-bootstrap";
import { FiArrowLeft, FiSend } from "react-icons/fi";
import api from "../../Utils/api";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import EditorRicoAnexos from "./EditorRicoAnexos";
import MensagemBalao from "./MensagemBalao";

const STATUS = {
  ABERTA: "Aberta",
  AGUARDANDO_USUARIO: "Aguardando você",
  AGUARDANDO_SUPORTE: "Aguardando suporte",
  ENCERRADA: "Encerrada",
};

const ITENS_POR_PAGINA = 8;

function formatarData(valor) {
  if (!valor) return "-";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "-";
  return data.toLocaleString("pt-BR");
}

// Historico de mensagens do proprio usuario (mesma ideia da aba Mensagens do
// GodMode, porem restrito as conversas abertas por ele), acessivel a partir
// do botao flutuante de contato.
function HistoricoContatoModal({ show, onHide }) {
  const { toast } = useFeedback();
  const [carregando, setCarregando] = useState(false);
  const [conversas, setConversas] = useState([]);
  const [status, setStatus] = useState("");
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [selecionada, setSelecionada] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const editorRef = useRef(null);

  const carregar = async (paginaAlvo = pagina) => {
    setCarregando(true);
    try {
      const { data } = await api.get("/api/contato/minhas-conversas", {
        params: { status: status || undefined, pagina: paginaAlvo, limite: ITENS_POR_PAGINA },
      });
      setConversas(data.data || []);
      setTotalPaginas(data.paginacao?.totalPaginas || 1);
      setTotalRegistros(data.paginacao?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || "Não foi possível carregar o histórico.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (show) carregar(1);
    setPagina(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, status]);

  const handleMudarPagina = (novaPagina) => {
    if (novaPagina < 1 || novaPagina > totalPaginas || novaPagina === pagina) return;
    setPagina(novaPagina);
    carregar(novaPagina);
  };

  const abrir = async (conversa) => {
    try {
      const { data } = await api.get(`/api/contato/conversas/${conversa.id}`);
      setSelecionada(data.conversa);
      setMensagens(data.mensagens || []);
      if (editorRef.current) editorRef.current.limpar();
    } catch (err) {
      toast.error(err.response?.data?.message || "Não foi possível abrir a conversa.");
    }
  };

  const responder = async () => {
    const conteudo = editorRef.current ? editorRef.current.obterConteudo() : null;
    if (!selecionada || !conteudo || (!conteudo.mensagem_texto.trim() && conteudo.anexos.length === 0)) {
      toast.warning("Escreva uma resposta antes de enviar.");
      return;
    }
    setEnviando(true);
    try {
      await api.post(`/api/contato/conversas/${selecionada.id}/responder`, {
        mensagem_html: conteudo.mensagem_html,
        mensagem_texto: conteudo.mensagem_texto,
        anexos: conteudo.anexos,
      });
      toast.success("Resposta enviada.");
      await abrir(selecionada);
      await carregar(pagina);
    } catch (err) {
      toast.error(err.response?.data?.message || "Não foi possível responder.");
    } finally {
      setEnviando(false);
    }
  };

  const fechar = () => {
    setSelecionada(null);
    setMensagens([]);
    onHide();
  };

  return (
    <Modal show={show} onHide={fechar} size="lg" centered scrollable>
      <Modal.Header closeButton>
        {selecionada ? (
          <div className="d-flex align-items-center gap-2">
            <Button variant="link" size="sm" className="p-0" onClick={() => setSelecionada(null)}>
              <FiArrowLeft />
            </Button>
            <Modal.Title>{`#${selecionada.id} - ${selecionada.titulo}`}</Modal.Title>
          </div>
        ) : (
          <Modal.Title>Histórico de mensagens</Modal.Title>
        )}
      </Modal.Header>
      <Modal.Body>
        {!selecionada && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Form.Select
                size="sm"
                style={{ maxWidth: 220 }}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={carregando}
              >
                <option value="">Todos os status</option>
                {Object.entries(STATUS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Form.Select>
              {carregando && <Spinner animation="border" size="sm" className="align-self-center" />}
            </div>

            <div style={{ minHeight: 360 }}>
              <Table hover responsive size="sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Assunto</th>
                    <th>Título</th>
                    <th>Status</th>
                    <th>Última mensagem</th>
                  </tr>
                </thead>
                <tbody>
                  {conversas.map((conversa) => (
                    <tr key={conversa.id} onClick={() => abrir(conversa)} style={{ cursor: "pointer" }}>
                      <td>{conversa.id}</td>
                      <td>{conversa.tipo_label}</td>
                      <td>{conversa.titulo}</td>
                      <td>
                        <Badge bg={conversa.status === "ENCERRADA" ? "secondary" : conversa.status === "AGUARDANDO_SUPORTE" ? "warning" : "primary"}>
                          {STATUS[conversa.status] || conversa.status}
                        </Badge>
                      </td>
                      <td>{formatarData(conversa.ultima_mensagem_em)}</td>
                    </tr>
                  ))}
                  {!carregando && conversas.length === 0 && (
                    <tr>
                      <td colSpan={5}>
                        <Alert variant="light" className="mb-0">Nenhuma mensagem enviada ainda.</Alert>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
              <small className="text-muted">
                {totalRegistros > 0 ? `${totalRegistros} conversa${totalRegistros === 1 ? "" : "s"}` : ""}
              </small>
              {totalPaginas > 1 && (
                <Pagination size="sm" className="mb-0">
                  <Pagination.Prev onClick={() => handleMudarPagina(pagina - 1)} disabled={pagina === 1 || carregando} />
                  {[...Array(totalPaginas)].map((_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === pagina}
                      onClick={() => handleMudarPagina(index + 1)}
                      disabled={carregando}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next onClick={() => handleMudarPagina(pagina + 1)} disabled={pagina === totalPaginas || carregando} />
                </Pagination>
              )}
            </div>
          </>
        )}

        {selecionada && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Badge bg="light" text="dark">{selecionada.tipo_label}</Badge>
              <Badge bg={selecionada.status === "ENCERRADA" ? "secondary" : "primary"}>
                {STATUS[selecionada.status] || selecionada.status}
              </Badge>
            </div>

            <div className="d-flex flex-column gap-2 mb-3">
              {mensagens.map((mensagem) => (
                <MensagemBalao
                  key={mensagem.id}
                  mensagem={mensagem}
                  autor={mensagem.origem === "GODMODE" ? "Duxen" : "Você"}
                  titulo={`Mensagem de ${mensagem.origem === "GODMODE" ? "Duxen" : "Você"}`}
                />
              ))}
            </div>

            {selecionada.status !== "ENCERRADA" ? (
              <Form.Group>
                <Form.Label>Responder</Form.Label>
                <EditorRicoAnexos ref={editorRef} placeholder="Escreva sua resposta para a Duxen..." disabled={enviando} />
              </Form.Group>
            ) : (
              <Alert variant="secondary" className="mb-0">Esta conversa foi encerrada.</Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={fechar}>Fechar</Button>
        {selecionada && selecionada.status !== "ENCERRADA" && (
          <Button onClick={responder} disabled={enviando}>
            {enviando ? <Spinner animation="border" size="sm" /> : <FiSend />} Enviar resposta
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default HistoricoContatoModal;
